import { Text } from '@eo-locale/react';
import type { FC } from 'react';
import { useQuery } from 'react-query';
import { HistoryApi } from '../api';
import { InnerPageSection, InnerPageTitle, Table } from '../components';
import { filterByCurrentAuthority } from '../utils/filterByCurrentAuthority/filterByCurrentAuthority';

interface BaseFunctionsRecord {
  name: string;
  start_date: string;
}

interface AuthorityMainRecord extends BaseFunctionsRecord {
  function: string;
  source: 'authorityMain';
}

interface GlobalAuthorityRecord extends BaseFunctionsRecord {
  function: string;
  source: 'globalAuthority';
}

interface StructureAuthorityRecord extends BaseFunctionsRecord {
  authority: string;
  source: 'structureAuthority';
}

interface CommissionsRecord extends BaseFunctionsRecord {
  function: string;
  authority: string;
  source: 'commissions';
}

type FunctionsRecord =
  | AuthorityMainRecord
  | GlobalAuthorityRecord
  | StructureAuthorityRecord
  | CommissionsRecord;

export const FunctionsPage: FC = () => {
  const { data } = useQuery('user-history', HistoryApi.fetch, { staleTime: 0 });

  const authorityMainRecords: FunctionsRecord[] =
    data?.authorityMain.data.filter(filterByCurrentAuthority())?.map(record => ({
      ...record,
      source: 'authorityMain' as const,
    })) || [];

  const globalAuthorityRecords: FunctionsRecord[] =
    data?.globalAuthority.data.filter(filterByCurrentAuthority())?.map(record => ({
      ...record,
      source: 'globalAuthority' as const,
    })) || [];

  const structureAuthorityRecords: FunctionsRecord[] =
    data?.structureAuthority.data.filter(filterByCurrentAuthority())?.map(record => ({
      ...record,
      source: 'structureAuthority' as const,
    })) || [];

  const commissionsRecords: FunctionsRecord[] =
    data?.commissions.data.filter(filterByCurrentAuthority())?.map(record => ({
      ...record,
      source: 'commissions' as const,
    })) || [];

  const allRecords: FunctionsRecord[] = [
    ...authorityMainRecords,
    ...globalAuthorityRecords,
    ...structureAuthorityRecords,
    ...commissionsRecords,
  ];

  const groupedData = allRecords.reduce<Record<string, FunctionsRecord[]>>((acc, record) => {
    const key =
      record.source === 'commissions'
        ? (record as CommissionsRecord).authority
        : record.name;
    if (!acc[key]) {
      acc[key] = [];
    }
    acc[key].push(record);
    return acc;
  }, {});

  const names = Object.keys(groupedData);

  return (
    <>
      {names.map(name => (
        <InnerPageSection key={name}>
          <InnerPageTitle>{name}</InnerPageTitle>
          <Table>
            <thead>
              <tr>
                <th>
                  <Text id="comissions:function" />
                </th>
                <th>
                  <Text id="comissions:start_date" />
                </th>
              </tr>
            </thead>
            <tbody>
              {groupedData[name].map((record, index) => {
                const displayValue =
                  record.source === 'structureAuthority' ? record.authority : record.function;
                return (
                  <tr key={`${name}-${index}`}>
                    <td>{displayValue}</td>
                    <td>{record.start_date}</td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </InnerPageSection>
      ))}
    </>
  );
};